import {
  CaretDownIcon,
  PlusIcon,
  PencilSimpleIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { DotsThreeVerticalIcon } from '@phosphor-icons/react/dist/ssr';
import { useState, useEffect, useRef, useCallback } from 'react';
import { ResponsiveContainer, BarChart, XAxis, YAxis, Bar } from 'recharts';
import { AccentButtonOutline } from '../../../../../../shared/Buttons';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import AddSavingsCategoryModal from '../../finances/widgets/monthly-budget/AddSavingsCategoryModal';
import {
  BudgetItemComputed,
  DeleteCustomBudgetItemRequestParams,
} from '../../../../../../types/apiTypes';
import { unicodeToEmoji } from '../../../../../../shared/emojiUnicode';
import { ClipLoader } from 'react-spinners';
import { formatCurrency } from '../../../../../../utils/CurrencyUtils';
import { Modal } from 'antd';
import { createPortal } from 'react-dom';
import { HttpStatusCode } from 'axios';
import toast from 'react-hot-toast';
import { useUser } from '../../../../../../utils/UserContext';
import useApi from '../../../../../../utils/AxiosWrapper';
import { useQueryClient } from '@tanstack/react-query';
import { DemoDataWrapper } from '../components/DemoDataBadge';
import { visibleBudgetBuckets } from '../../../../../../utils/budgetBuckets';

// Bounds how long we wait on the delete workflow before telling the customer it's still
// in flight. The workflow takes a couple of seconds in the normal case; the ceiling is for
// when it's queued behind another budget update holding the customer's lock.
const DELETE_POLL_INTERVAL_MS = 1000;
const DELETE_POLL_MAX_ATTEMPTS = 10;

interface SavingsBar extends BudgetItemComputed {
  isNegative: boolean;
  isOverBudget: boolean;
  Remaining?: number;
  PositiveSaved?: number;
  NegativeSaved?: number;
  BudgetLimit?: number;
  ExcessAmount?: number;
}

/**
 * One bucket's bar geometry.
 *
 * Extracted from the component so its branches are reachable from a test: recharts renders nothing
 * under jsdom's zero-sized ResponsiveContainer, so these values cannot be asserted through the DOM.
 */
export const toSavingsBar = (item: BudgetItemComputed): SavingsBar => {
  const goal = item?.budget_bucket_limit ?? 0;
  const net = item?.amount ?? 0;
  const isNegative = net < 0;
  const isOverBudget = net > goal;

  if (isNegative) {
    // For negative amounts: show red bar proportional to negative amount.
    // A bucket with no target reaches here — the visibility filter only drops one that is
    // empty AND targetless — and dividing by that zero gives Infinity, which recharts draws
    // as a silently broken bar rather than failing. With no target there is no proportion to
    // take, so an overdrawn one reads as fully negative, which is what it is.
    const negativePercentage =
      goal !== 0 ? (Math.abs(net) / Math.abs(goal)) * 100 : 100;
    return {
      ...item,
      NegativeSaved: negativePercentage,
      Remaining: 100 - negativePercentage,
      isNegative,
      isOverBudget,
    };
  } else if (isOverBudget) {
    // For over-budget: show gray bar for limit and green bar for excess
    const excess = net - goal;

    // A bucket with no target counts as over budget the moment it holds anything, and the
    // visibility filter only drops one that is empty AND targetless — so it lands here. With no
    // target there is no gray "up to the limit" portion to draw: all of it sits above a target
    // of zero, which is the full green bar `getPercent` already reports as 100% for this input.
    if (goal === 0) {
      return {
        ...item,
        BudgetLimit: 0,
        ExcessAmount: 100,
        isNegative,
        isOverBudget,
      };
    }

    // NOTE: the `* 100` below multiplies dollars rather than taking a percentage, so these land
    // far outside the chart's [0, 100] domain for every over-budget bucket. Pre-existing and
    // left alone deliberately — fixing it changes how every over-budget bucket renders, which is
    // a wider change than the targetless case this guard closes.
    return {
      ...item,
      BudgetLimit: (goal - excess) * 100,
      ExcessAmount: excess * 100,
      isNegative,
      isOverBudget,
    };
  } else {
    // Normal case
    return {
      ...item,
      PositiveSaved: (net / goal) * 100,
      Remaining: ((goal - net) / goal) * 100,
      isNegative,
      isOverBudget,
    };
  }
};

function SavingsWidget({
  seeAll,
  budgetItems,
  isLoading,
  isPreActive = false,
}: {
  seeAll?: boolean;
  budgetItems: BudgetItemComputed[];
  isLoading: boolean;
  isPreActive: boolean;
}) {
  const { screenView } = useScreenView();
  const [sliceLength, setSliceLength] = useState(4);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedBudgetItem, setSelectedBudgetItem] =
    useState<BudgetItemComputed | null>(null);
  const [popupVisibleMap, setPopupVisibleMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const [loadingDeleteMap, setLoadingDeleteMap] = useState<
    Map<string, boolean>
  >(new Map());
  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();
  const { userId } = useUser();

  const togglePopup = (budgetItemId: string | undefined) => {
    if (!budgetItemId) return;
    setPopupVisibleMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(budgetItemId, !newMap.get(budgetItemId));
      return newMap;
    });
  };

  const handleSetLoadingDelete = (loading: boolean, budgetItemId: string) => {
    setLoadingDeleteMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(budgetItemId, loading);
      return newMap;
    });
  };

  /**
   * POST /budgetItems/delete only *starts* the Temporal workflow that re-allocates the
   * category's balance and removes the row, so its 200 means "accepted", not "deleted".
   * Refetching once on that 200 races the workflow and almost always reads back the
   * category that is still there — which is what made the success toast look like a lie.
   * Poll instead, and only claim success once the category is actually gone.
   */
  const waitForBudgetItemDeletion = async (
    budgetItemId: string
  ): Promise<boolean> => {
    const queryKey = ['budgetItems', userId ?? ''];
    for (let attempt = 0; attempt < DELETE_POLL_MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) =>
        setTimeout(resolve, DELETE_POLL_INTERVAL_MS)
      );
      await queryClient.invalidateQueries({ queryKey });
      const items = queryClient.getQueryData<BudgetItemComputed[]>(queryKey);
      if (!items?.some(({ category }) => category === budgetItemId))
        return true;
    }
    return false;
  };

  const deleteBudgetItem = async (budgetItemId: string) => {
    handleSetLoadingDelete(true, budgetItemId);
    try {
      const customerId = userId ?? '';
      const response = await callProtectedApi(
        `/budgetItems/delete`,
        'post',
        {
          customer_id: customerId,
          category: budgetItemId,
        } as DeleteCustomBudgetItemRequestParams,
        'Error occurred while deleting savings category.'
      );
      if (response?.status === HttpStatusCode.Ok) {
        const isDeleted = await waitForBudgetItemDeletion(budgetItemId);
        if (isDeleted) {
          toast.success('Savings category deleted successfully');
        } else {
          toast(
            "We're still deleting this savings category. Refresh in a moment to see the change."
          );
        }
      }
    } catch (e: any) {
      console.error('Error occurred while deleting savings category: ', e);
    } finally {
      handleSetLoadingDelete(false, budgetItemId);
    }
  };

  const showDeleteConfirm = (dataSub: SavingsBar) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this savings category?',
      content: `This will permanently delete your "${dataSub.category_display_name}" savings goal.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        deleteBudgetItem(dataSub.category);
      },
    });
  };

  const handleClickOutside = (event: MouseEvent) => {
    const newMap = new Map(popupVisibleMap);
    let popupClicked = false;

    // Check if the click was outside any of the visible popups
    budgetItems?.forEach((dataSub) => {
      const popupRef = popupRefs.current[dataSub?.category ?? ''];
      const buttonRef = buttonRefs.current[dataSub?.category ?? ''];

      if (
        (popupRef && popupRef.contains(event.target as Node)) ||
        (buttonRef && buttonRef.contains(event.target as Node))
      ) {
        popupClicked = true;
      }
    });

    // If click was outside all popups and buttons, close all popups
    if (!popupClicked) {
      budgetItems?.forEach((dataSub) => {
        newMap.set(dataSub?.category ?? '', false);
      });
      setPopupVisibleMap(newMap);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupVisibleMap]);

  useEffect(() => {
    if (isPreActive) {
      setSliceLength(budgetItems?.length ?? 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPercent = useCallback((bi: SavingsBar | BudgetItemComputed) => {
    const goal = bi?.budget_bucket_limit ?? 0;
    const net = bi?.amount ?? 0;
    if (goal > 0) return Math.round((net / goal) * 100);
    // No goal set: if there’s activity, show 100%; else 0
    return net > 0 ? 100 : 0;
  }, []);

  // Normalize data so that Paid and Remaining are percentages of Total
  // Same visibility rule the Finances tab's Monthly Budgeted Savings uses — shared rather than
  // duplicated, because the two drifted: this widget dropped a bucket an admin had turned off
  // while the other drew it as a completed savings goal.
  const normalizedData = visibleBudgetBuckets(budgetItems)?.map(toSavingsBar);

  const renderBars = (dataSub: SavingsBar) => {
    if (dataSub?.isNegative) {
      return [
        <Bar
          key="negative"
          dataKey="NegativeSaved"
          fill="#C64949"
          stackId="a"
          className="z-20"
          radius={[10, 10, 10, 10]}
        />,
        <Bar
          key="remaining"
          dataKey="Remaining"
          fill="#E6E6E6"
          stackId="a"
          className="z-10"
          radius={[10, 10, 10, 10]}
        />,
      ];
    } else if (dataSub?.isOverBudget) {
      return [
        <Bar
          key="budgetLimit"
          dataKey="BudgetLimit"
          fill="#E6E6E6"
          stackId="a"
          className="z-20"
          radius={[10, 10, 10, 10]}
        />,
        <Bar
          key="excess"
          dataKey="ExcessAmount"
          fill="#6A9E68"
          stackId="a"
          className="z-10"
          radius={[10, 10, 10, 10]}
        />,
      ];
    } else if (dataSub?.PositiveSaved === 0 && dataSub?.Remaining === 100) {
      return (
        <Bar
          key="remaining_full"
          dataKey="Remaining"
          fill="#E6E6E6"
          stackId="a"
          radius={[10, 10, 10, 10]}
        />
      );
    } else if (dataSub?.PositiveSaved === 100 && dataSub?.Remaining === 0) {
      return (
        <Bar
          key="positive_full"
          dataKey="PositiveSaved"
          fill="#1D213B"
          stackId="a"
          radius={[10, 10, 10, 10]}
        />
      );
    } else {
      return [
        <Bar
          key="positive"
          dataKey="PositiveSaved"
          fill="#1D213B"
          stackId="a"
          radius={[10, 0, 0, 10]}
        />,
        <Bar
          key="remaining"
          dataKey="Remaining"
          fill="#E6E6E6"
          stackId="a"
          radius={[0, 10, 10, 0]}
        />,
      ];
    }
  };

  const renderBarChart = (dataSub: SavingsBar) => {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={[dataSub]}
          layout="vertical"
          margin={{ top: 5, bottom: 5 }}
        >
          <XAxis type="number" domain={[0, 100]} hide />
          <YAxis type="category" dataKey="name" hide />
          {renderBars(dataSub)}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderDotsMenu = (dataSub: SavingsBar) => {
    return (
      <>
        {!loadingDeleteMap.get(dataSub?.category) ? (
          <button
            ref={(el) => {
              buttonRefs.current[dataSub?.category ?? ''] = el;
            }}
            onClick={() => togglePopup(dataSub?.category)}
            className="relative disabled:cursor-not-allowed"
            disabled={isPreActive}
            aria-label={`Options for ${dataSub?.category_display_name}`}
          >
            <DotsThreeVerticalIcon size={24} weight="bold" />
            {popupVisibleMap.get(dataSub?.category ?? '') &&
              createPortal(
                <div
                  ref={(el) => {
                    popupRefs.current[dataSub?.category ?? ''] = el;
                  }}
                  className="fixed z-[9999] w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                  style={{
                    left:
                      buttonRefs.current[
                        dataSub?.category ?? ''
                      ]?.getBoundingClientRect().left ?? 0,
                    top:
                      buttonRefs.current[
                        dataSub?.category ?? ''
                      ]?.getBoundingClientRect().top ?? 0,
                    transform: 'translateX(-100%) translateX(-8px)',
                  }}
                >
                  <div>
                    <button
                      onClick={(e: any) => {
                        e.stopPropagation();
                        togglePopup(dataSub?.category ?? '');
                        setSelectedBudgetItem(dataSub);
                        setIsEditModalOpen(true);
                      }}
                      className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                    >
                      <PencilSimpleIcon size={20} />
                      <p>Edit</p>
                    </button>
                    <button
                      onClick={(e: any) => {
                        e.stopPropagation();
                        togglePopup(dataSub?.category ?? '');
                        showDeleteConfirm(dataSub);
                      }}
                      className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                    >
                      <TrashIcon size={20} />
                      <p>Delete</p>
                    </button>
                  </div>
                </div>,
                document.body
              )}
          </button>
        ) : (
          <ClipLoader
            color="#1D213B"
            loading={true}
            size={20}
            aria-label="Loading Spinner"
            data-testid="loader"
            className="h-full w-full justify-center"
          />
        )}
      </>
    );
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="flex w-[100%] flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <AddSavingsCategoryModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
            />
            <AddSavingsCategoryModal
              isOpen={isEditModalOpen}
              onClose={() => setIsEditModalOpen(false)}
              editBudgetItem={selectedBudgetItem ?? undefined}
            />
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Budgeted Savings
              </h2>
              <AccentButtonOutline
                onClick={() => setIsModalOpen(true)}
                addClass={`py-[4px] rounded-full disabled:cursor-not-allowed disabled:opacity-60`}
                disabled={isPreActive}
              >
                <div className="flex flex-row items-center space-x-2 align-middle">
                  <PlusIcon />
                  <p>Save for New Expense</p>
                </div>
              </AccentButtonOutline>
            </div>
            {!isLoading ? (
              <>
                {normalizedData && normalizedData?.length > 0 ? (
                  <div className="verflow-y-hidden grid w-full grid-cols-4 gap-4 border-b-[1px] border-bordergray p-4">
                    {normalizedData
                      ?.slice(0, sliceLength)
                      .map((dataSub: SavingsBar, idx) => (
                        <div
                          key={idx}
                          className="flex w-full flex-col gap-y-[8px] rounded-lg border-[1px] border-bordergray p-4"
                        >
                          <div className="flex flex-row items-center justify-between align-middle">
                            <h2 className="text-[14px] font-[600]">
                              {unicodeToEmoji(dataSub?.emoji_unicode)}
                              {' ' + dataSub?.category_display_name}
                            </h2>
                            {dataSub?.isCustom && renderDotsMenu(dataSub)}
                          </div>
                          <div className="flex flex-row items-center justify-between align-middle">
                            <h2 className="text-[16px] font-[500] text-graysecondary">
                              {formatCurrency(dataSub?.amount) +
                                ' / ' +
                                formatCurrency(dataSub?.budget_bucket_limit)}
                            </h2>
                            <h2
                              className={`text-[16px] font-[700] text-graysecondary ${
                                dataSub?.amount > 0 &&
                                dataSub?.Remaining &&
                                dataSub?.Remaining >= 0 &&
                                'text-graysecondary'
                              } ${dataSub?.isNegative && 'text-danger'} ${
                                dataSub?.isOverBudget && 'text-secondary'
                              } `}
                            >
                              {getPercent(dataSub)}%
                            </h2>
                          </div>
                          <div className="h-6 w-full">
                            {renderBarChart(dataSub)}
                          </div>
                          <div className="-mt-2 text-xs text-gray-500">
                            {dataSub.time_window_in_months === 1
                              ? 'Every month'
                              : `Every ${dataSub.time_window_in_months} months`}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center space-y-1">
                    <p className="text-[14px] leading-[20px] text-graysecondary">
                      No Savings Goals Found
                    </p>{' '}
                    <AccentButtonOutline
                      onClick={() => setIsModalOpen(true)}
                      disabled={isPreActive}
                      addClass={`py-[8px] rounded-full disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      <div className="flex flex-row items-center justify-center space-x-2 text-center align-middle">
                        <PlusIcon />
                        <p>Save for New Expense</p>
                      </div>
                    </AccentButtonOutline>
                  </div>
                )}
              </>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={isLoading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="my-8 flex h-full w-full items-center justify-center"
              />
            )}
            {seeAll && normalizedData && normalizedData?.length > 0 && (
              <div className="flex h-[48px] w-full justify-center">
                <button
                  onClick={() => {
                    if (sliceLength === 4) {
                      setSliceLength(budgetItems?.length);
                    } else {
                      setSliceLength(4);
                    }
                  }}
                  className="flex flex-row items-center justify-center space-x-2 align-middle font-[700] text-blue-500"
                >
                  <p>{sliceLength !== 4 ? 'See Less' : 'See All'}</p>{' '}
                  <CaretDownIcon
                    size={20}
                    weight="bold"
                    className={`${sliceLength !== 4 && 'rotate-180'}`}
                  />
                </button>
              </div>
            )}
          </div>
        );
      case 'tablet':
        return (
          <div className="flex w-[100%] flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <AddSavingsCategoryModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
            />
            <AddSavingsCategoryModal
              isOpen={isEditModalOpen}
              onClose={() => setIsEditModalOpen(false)}
              editBudgetItem={selectedBudgetItem ?? undefined}
            />
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray px-4 py-2 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Budgeted Savings
              </h2>
              <AccentButtonOutline
                onClick={() => setIsModalOpen(true)}
                addClass={`py-[8px] rounded-full disabled:cursor-not-allowed disabled:opacity-60`}
                disabled={isPreActive}
              >
                <div className="flex flex-row items-center space-x-2 align-middle">
                  <PlusIcon />
                  <p>Save for New Expense</p>
                </div>
              </AccentButtonOutline>
            </div>
            {!isLoading ? (
              <>
                {normalizedData && normalizedData?.length > 0 ? (
                  <div className="grid w-full grid-cols-2 items-start gap-4 border-b-[1px] border-bordergray p-4">
                    {normalizedData
                      ?.slice(0, sliceLength)
                      .map((dataSub: SavingsBar, idx) => (
                        <div
                          key={idx}
                          className="flex w-full flex-col gap-y-[8px] rounded-lg border-[1px] border-bordergray p-4"
                        >
                          <div className="flex flex-row items-center justify-between align-middle">
                            <h2 className="text-[14px] font-[600]">
                              {unicodeToEmoji(dataSub?.emoji_unicode)}
                              {' ' + dataSub?.category_display_name}
                            </h2>
                            {dataSub?.isCustom && renderDotsMenu(dataSub)}
                          </div>
                          <div className="flex flex-row items-center justify-between align-middle">
                            <h2 className="text-[16px] font-[500] text-graysecondary">
                              {formatCurrency(dataSub?.amount) +
                                ' / ' +
                                formatCurrency(dataSub?.budget_bucket_limit)}
                            </h2>
                            <h2
                              className={`text-[16px] font-[700] ${
                                dataSub?.amount > 0 &&
                                dataSub?.Remaining &&
                                dataSub?.Remaining >= 0 &&
                                'text-graysecondary'
                              } ${dataSub?.isNegative && 'text-danger'} ${
                                dataSub?.isOverBudget && 'text-secondary'
                              } `}
                            >
                              {getPercent(dataSub)}%
                            </h2>
                          </div>
                          <div className="h-6 w-full">
                            {renderBarChart(dataSub)}
                          </div>
                          <div className="-mt-2 text-xs text-gray-500">
                            {dataSub.time_window_in_months === 1
                              ? 'Every month'
                              : `Every ${dataSub.time_window_in_months} months`}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center space-y-1">
                    <p className="text-[14px] leading-[20px] text-graysecondary">
                      No Savings Goals Found
                    </p>{' '}
                    <AccentButtonOutline
                      onClick={() => setIsModalOpen(true)}
                      addClass={`py-[8px] rounded-full disabled:cursor-not-allowed disabled:opacity-60`}
                      disabled={isPreActive}
                    >
                      <div className="flex flex-row items-center justify-center space-x-2 text-center align-middle">
                        <PlusIcon />
                        <p>Save for New Expense</p>
                      </div>
                    </AccentButtonOutline>
                  </div>
                )}
              </>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={isLoading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="my-8 flex h-full w-full items-center justify-center"
              />
            )}
            {normalizedData && normalizedData?.length > 0 && (
              <div className="flex h-[50px] w-full flex-col items-center justify-center bg-white align-middle">
                <button
                  onClick={() => {
                    if (sliceLength === 4) {
                      setSliceLength(budgetItems?.length);
                    } else {
                      setSliceLength(4);
                    }
                  }}
                  className="flex flex-row items-center justify-center space-x-2 align-middle font-[700] text-blue-500"
                >
                  <p>{sliceLength !== 4 ? 'See Less' : 'See All'}</p>{' '}
                  <CaretDownIcon
                    size={20}
                    weight="bold"
                    className={`${sliceLength !== 4 && 'rotate-180'}`}
                  />
                </button>
              </div>
            )}
          </div>
        );
      case 'mobile':
        return (
          <div className="flex w-[100%] flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <AddSavingsCategoryModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
            />
            <AddSavingsCategoryModal
              isOpen={isEditModalOpen}
              onClose={() => setIsEditModalOpen(false)}
              editBudgetItem={selectedBudgetItem ?? undefined}
            />
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-3 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Budgeted Savings
              </h2>
            </div>
            {!isLoading ? (
              <>
                {normalizedData && normalizedData?.length > 0 ? (
                  <div className="grid w-full grid-cols-1 items-start gap-4 border-b-[1px] border-bordergray px-3 py-4">
                    <AccentButtonOutline
                      onClick={() => setIsModalOpen(true)}
                      addClass={`py-[8px] rounded-full disabled:cursor-not-allowed disabled:opacity-60`}
                      disabled={isPreActive}
                    >
                      <div className="flex flex-row items-center justify-center space-x-2 text-center align-middle">
                        <PlusIcon />
                        <p>Save for New Expense</p>
                      </div>
                    </AccentButtonOutline>
                    {normalizedData
                      ?.slice(0, sliceLength)
                      .map((dataSub: SavingsBar, idx) => (
                        <div
                          key={idx}
                          className="flex h-[130px] max-h-[130px] w-full flex-col justify-between rounded-lg border-[1px] border-bordergray p-4"
                        >
                          <div className="flex flex-row items-center justify-between align-middle">
                            <h2 className="text-[14px] font-[600]">
                              {unicodeToEmoji(dataSub?.emoji_unicode)}
                              {' ' + dataSub?.category_display_name}
                            </h2>
                            {dataSub?.isCustom && renderDotsMenu(dataSub)}
                          </div>
                          <div className="flex flex-row items-center justify-between align-middle">
                            <h2 className="text-[16px] font-[500] text-graysecondary">
                              {formatCurrency(dataSub?.amount) +
                                ' / ' +
                                formatCurrency(dataSub?.budget_bucket_limit)}
                            </h2>
                            <h2
                              className={`text-[16px] font-[700] ${
                                dataSub?.amount > 0 &&
                                dataSub?.Remaining &&
                                dataSub?.Remaining >= 0 &&
                                'text-graysecondary'
                              } ${dataSub?.isNegative && 'text-danger'} ${
                                dataSub?.isOverBudget && 'text-secondary'
                              } `}
                            >
                              {getPercent(dataSub)}%
                            </h2>
                          </div>
                          <div className="h-6 w-full">
                            {renderBarChart(dataSub)}
                          </div>
                          <div className="-mt-2 text-xs text-gray-500">
                            {dataSub.time_window_in_months === 1
                              ? 'Every month'
                              : `Every ${dataSub.time_window_in_months} months`}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center space-y-1">
                    <p className="text-[14px] leading-[20px] text-graysecondary">
                      No Savings Goals Found
                    </p>{' '}
                    <AccentButtonOutline
                      onClick={() => setIsModalOpen(true)}
                      addClass={`py-[8px] rounded-full disabled:cursor-not-allowed disabled:opacity-60`}
                      disabled={isPreActive}
                    >
                      <div className="flex flex-row items-center justify-center space-x-2 text-center align-middle">
                        <PlusIcon />
                        <p>Save for New Expense</p>
                      </div>
                    </AccentButtonOutline>
                  </div>
                )}
              </>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={isLoading}
                size={40}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="my-8 flex h-full w-full items-center justify-center"
              />
            )}
            {normalizedData && normalizedData?.length > 0 && (
              <div className="flex h-[50px] w-full flex-col items-center justify-center bg-white align-middle">
                <button
                  onClick={() => {
                    if (sliceLength === 4) {
                      setSliceLength(budgetItems?.length);
                    } else {
                      setSliceLength(4);
                    }
                  }}
                  className="flex flex-row items-center justify-center space-x-2 align-middle font-[700] text-blue-500"
                >
                  <p>{sliceLength !== 4 ? 'See Less' : 'See All'}</p>{' '}
                  <CaretDownIcon
                    size={20}
                    weight="bold"
                    className={`${sliceLength !== 4 && 'rotate-180'}`}
                  />
                </button>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <DemoDataWrapper showBadge={isPreActive}>
      {renderScreenView(screenView)}
    </DemoDataWrapper>
  );
}

export default SavingsWidget;
